import { useCallback, useEffect, useState } from 'react';
import { requestJson } from '../services/api';

export default function useLoanData(user, view, enabled = true) {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  const role = user?.role;
  const employeeId = user?.employee_id;
  const canViewDisbursed = user?.can_view_disbursed;

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let timer;
    let inFlight = false;
    let controller;
    const load = async () => {
      if (stopped || inFlight) return;
      clearTimeout(timer);
      if (document.hidden) {
        timer = setTimeout(load, 30000);
        return;
      }
      inFlight = true;
      controller = new AbortController();
      setLoading(true);
      try {
        const data = await requestJson('list_loans.php', {
          method: 'POST', signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role, employee_id: employeeId, can_view_disbursed: canViewDisbursed || 'no', view })
        });
        if (data.status !== 'success' || !Array.isArray(data.loans)) throw new Error('Invalid loan response');
        if (!stopped) {
          const rows = data.loans.filter(loan => {
            const archived = String(loan.is_archived) === '1';
            if (view === 'active') return !archived;
            if (view === 'archived') return archived;
            if (view === 'disbursed') return !archived && loan.loan_status === 'Disbursed';
            return true;
          });
          setLoans(rows);
          setError('');
        }
      } catch (err) {
        if (!stopped) setError('Unable to refresh loans. Any previously loaded data is still shown. Retrying automatically.');
      } finally {
        inFlight = false;
        if (!stopped) {
          setLoading(false);
          timer = setTimeout(load, 30000);
        }
      }
    };
    const onVisible = () => { if (!document.hidden) load(); };
    load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [role, employeeId, canViewDisbursed, view, enabled, revision]);

  return { loans, setLoans, loading, error, refresh };
}
